import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import disableDevtool from 'disable-devtool';
import { Md5BypassService } from './md5-bypass.service';

// Declarar el tipo global de Dynatrace RUM
declare global {
  interface Window {
    dtrum?: {
      enterAction: (name: string) => number;
      leaveAction: (actionId: number) => void;
      reportError: (error: Error) => void;
      getAndClearMarks: () => any[];
      addEnterActionListener: (callback: (actionId: number, name: string) => void) => void;
      identifyUser: (userId: string) => void;
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class DevtoolsDetectorService {
  private router = inject(Router);
  private md5BypassService = inject(Md5BypassService);
  private isDynatracePresent = false;

  /**
   * Detecta si la solicitud proviene de un sintético de Dynatrace
   * Los sintéticos usan User-Agents específicos que podemos identificar
   */
  private isDynatraceSynthetic(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const syntheticPatterns = [
      'dynatracesynthetic',
      'dynatrace synthetic',
      'ruxitsynthetic',
      'dynatrace',
    ];

    for (const pattern of syntheticPatterns) {
      if (userAgent.includes(pattern)) {
        console.log('Dynatrace Synthetic detectado (User-Agent)');
        return true;
      }
    }

    return false;
  }

  /**
   * Detecta si el agente RUM de Dynatrace está cargado en la página
   * Verifica tanto el objeto global como scripts en el DOM
   */
  private detectDynatraceAgent(): boolean {
    // Verificar objeto global de Dynatrace
    if (typeof window !== 'undefined' && window.dtrum) {
      console.log('Dynatrace RUM Agent detectado (objeto global)');
      return true;
    }

    // Verificar scripts de Dynatrace en el DOM
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      const src = scripts[i].src || '';
      if (src.includes('dynatrace') || src.includes('ruxitagent')) {
        console.log('Dynatrace RUM Agent detectado (script en DOM)');
        return true;
      }
    }

    return false;
  }

  /**
   * Inicializa el detector de herramientas de desarrollo si está habilitado en el entorno actual
   * Incluye soporte para coexistencia con Dynatrace RUM
   *
   * Activación por ambiente:
   * - Dev: disableDevTools=false → NO se activa
   * - UAT: disableDevTools=true → SÍ se activa
   * - Prod: disableDevTools=true → SÍ se activa
   */
  initialize(): void {
    const dynatraceConfig = (environment as any).dynatraceConfig;

    // Verificar si está habilitado para este entorno
    if (!environment.disableDevTools) {
      console.log('DevTools detector desactivado para este entorno');
      return;
    }

    // Verificar si es un sintético de Dynatrace (no bloquear)
    if (this.isDynatraceSynthetic()) {
      console.log('Dynatrace Synthetic detectado - protección desactivada para permitir monitoreo');
      return;
    }

    // Verificar si hay un bypass MD5 válido
    if (this.md5BypassService.checkMd5Bypass()) {
      console.log('DevTools permitidas mediante bypass MD5 válido');
      return;
    }

    // Esperar para permitir que Dynatrace se inicialice completamente
    const initDelay = dynatraceConfig?.initDelay || 1500;

    setTimeout(() => {
      this.isDynatracePresent = this.detectDynatraceAgent();
      this.initializeProtection();
    }, initDelay);
  }

  /**
   * Inicializa la protección de DevTools con configuración adaptada según
   * la presencia de Dynatrace para garantizar compatibilidad
   */
  private initializeProtection(): void {
    try {
      // Generar el hash MD5 para la configuración de disable-devtool
      const md5Hash = environment.md5Config ? this.md5BypassService.generateMd5Hash() : undefined;

      // Configuración base de disable-devtool
      const config: Parameters<typeof disableDevtool>[0] = {
        // Hash MD5 para bypass
        md5: md5Hash,
        // Nombre del parámetro URL para el bypass MD5
        tkName: environment.md5Config?.urlParamName || 'ddtk',
        // Si se detectan las herramientas de desarrollo
        ondevtoolopen: () => {
          console.log('DevTools detectado, redirigiendo...');
          this.handleDevToolsOpened();
        },
        // Intervalo de detección: menos agresivo si Dynatrace está presente
        interval: this.isDynatracePresent ? 3000 : 1000,
        // Deshabilitar menú contextual (clic derecho)
        disableMenu: true,
        // Deshabilitar todos los atajos de teclado para las herramientas de desarrollo
        disableSelect: true,
      };

      // Ajustes adicionales cuando Dynatrace está presente
      if (this.isDynatracePresent) {
        // No interferir con iframes (Session Replay de Dynatrace puede usarlos)
        (config as any).disableIframeParents = false;
        // Continuar monitoreando incluso después de detectar DevTools
        (config as any).clearIntervalWhenDevOpenTrigger = false;

        console.log('DevTools detector configurado en modo compatible con Dynatrace');
      }

      // Inicializar disable-devtool
      disableDevtool(config);

      const modeInfo = this.isDynatracePresent ? '(modo Dynatrace)' : '(modo estándar)';
      console.log(`DevTools detector inicializado correctamente ${modeInfo}`);

      // En entornos de desarrollo, mostrar información de bypass
      if (!environment.production) {
        this.md5BypassService.logBypassInfo();
      }
    } catch (error) {
      console.error('Error al inicializar el detector de DevTools:', error);
    }
  }

  /**
   * Verifica si existe un parámetro secreto en la URL que permita usar DevTools
   */
  private checkSecretParam(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has(environment.md5Config.devToolsSecretParam);
  }

  /**
   * Maneja la acción cuando se detectan las herramientas de desarrollo abiertas
   */
  private handleDevToolsOpened(): void {
    if (environment.devToolsRedirectUrl) {
      // Verificar si la URL es externa o interna
      if (this.isExternalUrl(environment.devToolsRedirectUrl)) {
        // Para URLs externas, usar window.location.href
        console.log('Redirigiendo a URL externa:', environment.devToolsRedirectUrl);
        window.location.href = environment.devToolsRedirectUrl;
      } else {
        // Para URLs internas, usar Angular Router
        console.log('Redirigiendo a ruta interna:', environment.devToolsRedirectUrl);
        this.router.navigateByUrl(environment.devToolsRedirectUrl);
      }
    } else {
      // Fallback: recargar la página si no hay URL configurada
      console.log('No hay URL de redirección configurada, recargando página');
      window.location.reload();
    }
  }

  /**
   * Verifica si una URL es externa (tiene protocolo http/https) o interna
   */
  private isExternalUrl(url: string): boolean {
    try {
      // Si la URL comienza con http:// o https://, es externa
      return url.startsWith('http://') || url.startsWith('https://');
    } catch (error) {
      console.error('Error verificando URL:', error);
      return false;
    }
  }

  /**
   * Método público para verificar si Dynatrace está activo
   * Útil para debugging y monitoreo
   */
  isDynatraceActive(): boolean {
    return this.isDynatracePresent;
  }
}
